'use client'

import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { useProduct, useDeleteProducts } from '@/lib/api/products'
import { useToast } from '@/components/ui/use-toast'

interface DeleteProductModalProps {
    productId?: string
    onClose: () => void
    onSuccess?: () => void
}

interface DeleteProductModalContentProps {
    productId: string
    onClose: () => void
    onSuccess?: () => void
}

export const DeleteProductModal = ({ productId, onClose, onSuccess }: DeleteProductModalProps) => {
    if (!productId) return null
    return <DeleteProductModalContent productId={productId} onClose={onClose} onSuccess={onSuccess} />
}

const DeleteProductModalContent = ({ productId, onClose, onSuccess }: DeleteProductModalContentProps) => {
    const { t } = useTranslation('products')
    const { t: tCommon } = useTranslation('common')
    const { toast } = useToast()
    const { data: product, isPending: isLoadingProduct } = useProduct(productId)
    const deleteProducts = useDeleteProducts()

    const handleDelete = async () => {
        if (!productId) return

        try {
            await deleteProducts.mutateAsync({ productIds: [productId] })

            toast({
                title: t('productDeleteSuccess'),
                description: t('productDeleted', { name: product?.name || t('product') }),
            })

            onSuccess?.()
            onClose()
        } catch (error) {
            toast({
                title: tCommon('error'),
                description: t('deleteProductError'),
                variant: 'destructive',
            })
        }
    }

    const productName = product?.name || t('product')

    return (
        <AlertDialog open={true} onOpenChange={onClose}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>{t('deleteProduct')}</AlertDialogTitle>
                    <AlertDialogDescription>
                        {isLoadingProduct ? t('loadingProductDetails') : t('deleteProductConfirmation', { name: productName })}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={deleteProducts.isPending}>
                        {tCommon('cancel')}
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={handleDelete}
                        disabled={deleteProducts.isPending || isLoadingProduct}
                    >
                        {deleteProducts.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {tCommon('delete')}
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}

